import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, SpeedDial, SpeedDialAction, SpeedDialIcon, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Clear } from '@mui/icons-material';
import Loader from '../components/loader';
import Device from '../components/mainScreen/device/device';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DateContext } from '../context/dateProvider';
import dayjs from 'dayjs';
import DialogDatesSelecter from '../components/dialogDatesSelecter';

const Aside = ({ devices, setDevices, isLoading, isMobileDevicesVisible, setIsMobileDevicesVisible, trackersGroups, showData, setShowData, setIsRecoveredReports }) => {
    const { startDate, endDate, setStartDate, setEndDate, setCustomDerivedDates } = useContext(DateContext);
    const [isRetracted, setIsRetracted] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [collapsedSections, setCollapsedSections] = useState({});

    const [openDateModal, setOpenDateModal] = useState(false);

    const toggleAllDevices = () => {
        const allVisible = devices.every(device => device.visible);
        const updatedDevices = devices.map(device => ({ ...device, visible: !allVisible }));
        setDevices(updatedDevices);
    };

    const getCheckboxState = () => {
        const allVisible = devices.every(device => device.visible);
        const noneVisible = devices.every(device => !device.visible);

        if (allVisible) return true;
        if (noneVisible) return false;
        return 'indeterminate';
    };

    const toggleDeviceVisibility = (deviceId) => {
        setDevices(prevDevices => {
            const deviceIndex = prevDevices.findIndex(device => device.id === deviceId);
            const updatedDevice = { ...prevDevices[deviceIndex], visible: !prevDevices[deviceIndex].visible };
            return [...prevDevices.slice(0, deviceIndex), updatedDevice, ...prevDevices.slice(deviceIndex + 1)];
        });
    };

    const groupDevicesByGroupId = (devices, trackersGroups) => {
        const groupMap = trackersGroups.reduce((acc, group) => {
            acc[group.id] = {
                title: group.title,
                color: group.color,
                devices: []
            };
            return acc;
        }, {});

        const untrackedGroup = {
            title: "Objetos rastreables",
            color: "000000", // Color predeterminado para este grupo
            devices: []
        };

        devices.forEach(device => {
            if (groupMap[device.group_id]) {
                groupMap[device.group_id].devices.push(device);
            } else {
                untrackedGroup.devices.push(device);
            }
        });

        const filteredGroups = Object.values(groupMap).filter(group => group.devices.length > 0);
        if (untrackedGroup.devices.length > 0) {
            return [untrackedGroup, ...filteredGroups];
        }

        return filteredGroups;
    };

    const toggleGroupVisibility = (groupTitle) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [groupTitle]: !prev[groupTitle],
        }));
    };

    const getGroupCheckboxState = (groupDevices) => {
        const allVisible = groupDevices.every(device => device.visible);
        const noneVisible = groupDevices.every(device => !device.visible);

        if (allVisible) return true;
        if (noneVisible) return false;
        return "indeterminate";
    };

    const toggleGroupDevices = (groupDevices, newState) => {
        setDevices(prevDevices =>
            prevDevices.map(device =>
                groupDevices.some(groupDevice => groupDevice.id === device.id)
                    ? { ...device, visible: newState }
                    : device
            )
        );
    };

    const [searchTerm, setSearchTerm] = useState("");

    const filteredDevices = devices.filter(device =>
        device.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Obtener estado del checkbox de búsqueda
    const getFilteredCheckboxState = () => {
        if (filteredDevices.length === 0) return false;
        const allVisible = filteredDevices.every(device => device.visible);
        const noneVisible = filteredDevices.every(device => !device.visible);
        if (allVisible) return true;
        if (noneVisible) return false;
        return "indeterminate";
    };

    // Activar/desactivar solo los dispositivos filtrados
    const toggleFilteredDevices = () => {
        const newState = getFilteredCheckboxState() !== true;
        setDevices(prevDevices =>
            prevDevices.map(device =>
                filteredDevices.some(filteredDevice => filteredDevice.id === device.id)
                    ? { ...device, visible: newState }
                    : device
            )
        );
    };

    const asideRef = useRef();

    useEffect(() => {
        // scroll lock
        document.body.classList.toggle('no-scroll', isMobileDevicesVisible);

        const handleClickOutside = (event) => {
            const clickedInsideAside = asideRef.current?.contains(event.target);
            const clickedToggle = event.target.closest('.mobile_devices_button'); // ⬅️ ignorar botón
            if (!clickedInsideAside && !clickedToggle) {
                setIsMobileDevicesVisible(false);
            }
        };

        if (isMobileDevicesVisible) {
            // Mejor usa 'click' para evitar carreras con onClick de React
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            document.body.classList.remove('no-scroll');
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isMobileDevicesVisible]);



    return (
        <aside ref={asideRef} className={`main-screen_left_panel ${isRetracted ? 'retracted' : ''} ${isMobileDevicesVisible ? 'visible' : 'hidden'}`}>
            <div className={`dashboard_card main-screen_devices_container`}>
                <div className='main-screen_devices_header_container'>
                    <span className='main-screen_devices_header'>Dispositivos</span>
                    <button className='main-screen_left_panel_btn' onClick={() => setIsRetracted(prev => !prev)}>
                        <span className="material-symbols-outlined">menu_open</span>
                    </button>
                </div>
                <div
                    className={`search-field-wrapper ${isRetracted ? 'retracted' : 'expanded'}`}
                >
                    <TextField
                        label="Buscar"
                        variant="outlined"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            marginLeft: '8px',
                            maxWidth: '93%',
                            '& label': {
                                fontSize: '0.8rem',
                                transform: 'translate(14px, 10px) scale(1)', // Alineado inicial
                                transition: 'all 0.2s ease-out' // Transición suave al subir
                            },
                            '& .MuiInputLabel-shrink': {
                                transform: 'translate(14px, -8px) scale(0.85) !important', // Movimiento al enfocar
                            },
                            '& input': {
                                padding: '10px 14px',
                                fontSize: '0.8rem'
                            }
                        }}
                        slotProps={{
                            input: {
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm("")}>
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </div>
                <div className="checkbox-container">
                    <label className="custom-checkbox">
                        <span className='checkbox_text'>
                            {searchTerm
                                ? `Encontrados (${filteredDevices.length})`
                                : `Todos (${devices.length})`}
                        </span>
                        <input
                            type="checkbox"
                            checked={getFilteredCheckboxState() === true}
                            ref={input => input && (input.indeterminate = getFilteredCheckboxState() === "indeterminate")}
                            onChange={toggleFilteredDevices}
                        />
                        <span className="checkmark"></span>
                    </label>
                </div>

                <div className='devices_container'>
                    {isLoading ? (
                        <Loader progress={null} />
                    ) : (
                        searchTerm && !isRetracted ? (
                            // Mostrar solo dispositivos filtrados cuando hay búsqueda
                            filteredDevices.length > 0 ? (
                                <div className="group_devices">
                                    {filteredDevices.map((device) => (
                                        <Device
                                            key={device.id}
                                            id={device.id}
                                            name={device.label}
                                            color={device.color}
                                            iconColor={device.iconColor}
                                            isVisible={device.visible}
                                            onToggle={() => toggleDeviceVisibility(device.id)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <span style={{ fontSize: '0.7rem', margin: 'auto', opacity: '0.8', fontStyle: 'italic' }}>No se encontraron dispositivos</span>
                            )
                        ) : (
                            // Mostrar dispositivos agrupados cuando no hay búsqueda
                            groupDevicesByGroupId(devices, trackersGroups).map(group => (
                                <div key={group.title} className="device-group">
                                    <div className={`group_header ${isRetracted ? 'retracted' : ''}`} onClick={() => toggleGroupVisibility(group.title)}>
                                        <label className="custom-checkbox" onClick={(event) => event.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={getGroupCheckboxState(group.devices) === true}
                                                ref={(input) => input && (input.indeterminate = getGroupCheckboxState(group.devices) === "indeterminate")}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    const newState = getGroupCheckboxState(group.devices) !== true;
                                                    toggleGroupDevices(group.devices, newState);
                                                }}
                                            />
                                            <span className="checkmark"></span>
                                        </label>
                                        <span className='group_title'>{group.title} ({group.devices.length})</span>
                                        <span className="material-symbols-outlined">
                                            {collapsedGroups[group.title] ? "keyboard_arrow_down" : "keyboard_arrow_up"}
                                        </span>
                                    </div>
                                    {!collapsedGroups[group.title] && (
                                        <div className='group_devices'>
                                            {group.devices.map((device) => (
                                                <Device
                                                    key={device.id}
                                                    id={device.id}
                                                    name={device.label}
                                                    color={device.color}
                                                    iconColor={device.iconColor}
                                                    isVisible={device.visible}
                                                    onToggle={() => toggleDeviceVisibility(device.id)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )
                    )}
                </div>

                {/* <div className='devices_container'>
                    {isLoading ? (
                        <Loader progress={null} />
                    ) : (
                        Array.isArray(devices) && devices.length > 0 ? (
                            groupDevicesByGroupId(devices, trackersGroups).map(group => (
                                <div key={group.title} className="device-group">
                                    <div className={`group_header ${isRetracted ? 'retracted' : ''}`} onClick={(e) => { toggleGroupVisibility(group.title); }}>
                                        <label className="custom-checkbox" onClick={(event) => event.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={getGroupCheckboxState(group.devices) === true}
                                                ref={(input) => input && (input.indeterminate = getGroupCheckboxState(group.devices) === "indeterminate")}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    const newState = getGroupCheckboxState(group.devices) !== true;
                                                    toggleGroupDevices(group.devices, newState);
                                                }}
                                            />
                                            <span className="checkmark"></span>
                                        </label>
                                        <span className='group_title'>{group.title} ({group.devices.length})</span>
                                        <span className="material-symbols-outlined">{collapsedGroups[group.title] ? "keyboard_arrow_down" : "keyboard_arrow_up"}</span>
                                    </div>
                                    {!collapsedGroups[group.title] && (
                                        <div className='group_devices'>
                                            {group.devices.map((device, index) => (
                                                <Device
                                                    key={index}
                                                    id={device.id}
                                                    name={device.label}
                                                    color={device.color}
                                                    iconColor={device.iconColor}
                                                    isVisible={device.visible}
                                                    onToggle={() => {
                                                        toggleDeviceVisibility(device.id)
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p>No se encontraron dispositivos</p>
                        )
                    )}
                </div> */}
            </div>
            {showData &&
                <>
                    <SpeedDial
                        ariaLabel="Acciones"
                        sx={{
                            position: 'fixed',
                            bottom: 23,
                            left: 35,
                            '& .MuiFab-primary': {
                                width: '3rem',
                                height: '3rem',
                                borderRadius: 4 // Cambia este valor según lo redondo que lo quieras
                            }
                        }}
                        icon={<span className="material-symbols-outlined" style={{ fontSize: '1.4rem' }}>menu</span>}
                        direction="right"
                    >
                        <SpeedDialAction
                            icon={<span className="material-symbols-outlined">event</span>}
                            tooltipTitle="Seleccionar fechas"
                            onClick={() => {
                                setOpenDateModal(true);
                            }}
                        />
                        <SpeedDialAction
                            icon={<span className="material-symbols-outlined">source_notes</span>}
                            tooltipTitle="Regenerar reportes"
                            onClick={() => {
                                setIsRecoveredReports(false);
                                setShowData(false);
                            }}
                        />
                    </SpeedDial>
                    <DialogDatesSelecter openDateModal={openDateModal} setOpenDateModal={setOpenDateModal} setShowData={setShowData} />
                </>
            }
        </aside>
    )
}

export default Aside